'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import Image from 'next/image'
import { Dialog } from '@headlessui/react'
import { X, ChevronDown, ChevronLeft, ChevronRight, MapPin, Clock, Ruler } from 'lucide-react'
import { Listbox } from '@headlessui/react'
import { useLanguage } from '@/contexts/LanguageContext'
import { portfolioTranslations } from '@/translations/portfolio'

type ProjectId = 'modernGarden' | 'lawnRenovation' | 'seasonalPlanting' | 'patioInstallation' | 'waterFeature' | 'gardenMaintenance'
type CategoryId = 'all' | 'design' | 'restoration' | 'lawnCare' | 'planting'

interface Project {
  id: ProjectId
  image: string
}

const projects: Project[] = [
  {
    id: 'modernGarden',
    image: '/images/portfolio/before-1.webp',
  },
  {
    id: 'lawnRenovation',
    image: '/images/portfolio/before-2.webp',
  },
  {
    id: 'seasonalPlanting',
    image: '/images/portfolio/before-3.webp',
  },
  {
    id: 'patioInstallation',
    image: '/images/portfolio/before-4.webp',
  },
  {
    id: 'waterFeature',
    image: '/images/portfolio/before-5.webp',
  },
  {
    id: 'gardenMaintenance',
    image: '/images/portfolio/before-6.webp',
  },
]

const categories: CategoryId[] = ['all', 'design', 'restoration', 'lawnCare', 'planting']

export default function Portfolio() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryId>('all')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const { language } = useLanguage()
  const t = portfolioTranslations[language]

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(project => t.projects[project.id].category === selectedCategory)

  const nextProject = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredProjects.length)
  }

  const prevProject = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredProjects.length) % filteredProjects.length)
  }

  // Reset currentIndex when category changes
  useEffect(() => {
    setCurrentIndex(0)
  }, [selectedCategory])

  // Ensure we have valid projects before rendering
  if (!filteredProjects.length) {
    return null
  }

  const currentProject = filteredProjects[currentIndex]

  return (
    <section
      id="portfolio"
      className="py-24 bg-gradient-to-b from-gray-50 to-gray-100"
      aria-labelledby="portfolio-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            {t.title}
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Mobile Dropdown */}
        <div className="lg:hidden relative mb-8 mt-8">
          <Listbox value={selectedCategory} onChange={setSelectedCategory}>
            {({ open }: { open: boolean }) => (
              <div>
                <Listbox.Button className={`w-full p-4 text-left bg-white rounded-xl shadow-sm focus:outline-none transition-all duration-200 border ${open ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-300'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-gray-900">{t.categories[selectedCategory]}</span>
                    <ChevronDown className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  </div>
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-sm border border-gray-100 max-h-60 overflow-auto focus:outline-none">
                  {categories.map((category) => (
                    <Listbox.Option
                      key={category}
                      value={category}
                      className={({ active }: { active: boolean }) =>
                        `relative cursor-pointer select-none px-6 py-4 ${active ? 'bg-gray-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
                      }
                    >
                      {({ selected }: { selected: boolean }) => (
                        <span className={`block text-lg font-medium ${selected ? 'text-gray-900' : 'text-gray-500'}`}>{t.categories[category]}</span>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            )}
          </Listbox>
        </div>

        {/* Desktop Button Group */}
        <div className="mt-12 hidden lg:flex flex-wrap justify-center gap-3 px-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 border focus:outline-none ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white border-gray-800 shadow-md hover:shadow-lg'
                  : 'bg-white text-gray-600 border-gray-200 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {t.categories[category]}
            </button>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="mt-12 lg:hidden">
          <div className="relative mx-auto max-w-4xl px-4">
            <div className="relative">
              {currentProject && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="relative bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
                  >
                    <div className="relative h-72">
                      <Image
                        src={currentProject.image}
                        alt={t.projects[currentProject.id].title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw"
                        priority
                      />
                    </div>
                    <div className="p-8">
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-900">
                        {t.categories[t.projects[currentProject.id].category as keyof typeof t.categories]}
                      </span>
                      <h3 className="mt-4 text-xl font-semibold text-gray-900">
                        {t.projects[currentProject.id].title}
                      </h3>
                      <p className="mt-3 text-gray-600 leading-relaxed">
                        {t.projects[currentProject.id].description}
                      </p>
                      {/* Mobile & Tablet: Full-width button below text */}
                      <button
                        onClick={() => setSelectedProject(currentProject)}
                        className="w-full mt-6 px-6 py-4 bg-emerald-700 text-white rounded-xl text-base font-medium hover:bg-emerald-800 transition-all duration-300 shadow-lg"
                      >
                        {t.viewDetails}
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              {/* Navigation Buttons */}
              <div className="absolute -left-4 -right-4 top-1/2 flex -translate-y-1/2 justify-between">
                <button
                  onClick={prevProject}
                  onMouseUp={(e) => e.currentTarget.blur()}
                  className="rounded-xl bg-white p-3 ring-1 ring-gray-200 transition-all hover:bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  aria-label="Previous project"
                >
                  <ChevronLeft className="h-6 w-6 font-normal" />
                </button>
                <button
                  onClick={nextProject}
                  onMouseUp={(e) => e.currentTarget.blur()}
                  className="rounded-xl bg-white p-3 ring-1 ring-gray-200 transition-all hover:bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  aria-label="Next project"
                >
                  <ChevronRight className="h-6 w-6 font-normal" />
                </button>
              </div>
            </div>

            {/* Progress Dots */}
            <div className="mt-8 flex justify-center">
              <div className="flex gap-2">
                {filteredProjects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    className={`h-6 w-6 mx-1 rounded-full transition-all duration-300 ease-in-out ${
                      index === currentIndex 
                        ? 'bg-gray-800 shadow-sm' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Grid */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-12 hidden lg:grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 cursor-pointer"
              onClick={() => setSelectedProject(project)}
            >
              <div className="relative h-72 overflow-hidden">
                <Image
                  src={project.image}
                  alt={t.projects[project.id].title}
                  fill
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className="object-cover transform group-hover:scale-105 transition-transform duration-500"
                  priority={false}
                  quality={85}
                />
                {/* Overlay - visible on hover */}
                <div className="absolute inset-0 bg-gray-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 lg:block" />
                {/* Bottom button - visible on mobile & tablet, hover on desktop */}
                <div className="absolute inset-0 flex items-end justify-center pb-6 lg:opacity-0 lg:group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProject(project);
                    }}
                    className="px-6 py-3 bg-gray-900/80 backdrop-blur-sm text-white rounded-xl text-sm font-medium hover:bg-gray-800/90 transition-all duration-300 shadow-lg lg:bg-gray-900 lg:text-white lg:hover:bg-gray-800"
                  >
                    {t.viewDetails}
                  </button>
                </div>
                {/* Mobile: Subtle tap indicator in bottom-right corner */}
                <div className="absolute bottom-3 right-3 md:hidden">
                  <div className="bg-black/20 backdrop-blur-sm rounded-full p-1.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-900">
                  {t.categories[t.projects[project.id].category as keyof typeof t.categories]}
                </span>
                <h3 className="mt-4 text-xl font-semibold text-gray-900 group-hover:text-gray-900 transition-colors duration-300">
                  {t.projects[project.id].title}
                </h3>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  {t.projects[project.id].description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Project Dialog */}
      <Dialog
        open={!!selectedProject}
        onClose={() => setSelectedProject(null)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl hide-scrollbar">
            {selectedProject && (
              <div className="relative">
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 z-10 rounded-xl bg-white/90 backdrop-blur-sm p-2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
                
                {/* Project Image */}
                <div className="relative h-80">
                  <Image
                    src={t.projects[selectedProject.id].afterImage || selectedProject.image}
                    alt={t.projects[selectedProject.id].title}
                    fill
                    className="object-cover rounded-t-2xl"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>

                {/* Project Content */}
                <div className="p-4 md:p-8">
                  {/* Header */}
                  <div className="mb-6">
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-900">
                      {t.categories[t.projects[selectedProject.id].category as keyof typeof t.categories]}
                    </span>
                    <Dialog.Title className="mt-4 text-xl md:text-3xl font-bold text-gray-900">
                      {t.projects[selectedProject.id].title}
                    </Dialog.Title>
                    <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed">
                      {t.projects[selectedProject.id].description}
                    </p>
                  </div>

                  {/* Specifications */}
                  {t.projects[selectedProject.id].specifications && (
                    <div className="mb-8">
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{t.modal.specifications}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-600">{t.modal.projectType}</span>
                          <p className="text-gray-900">{t.projects[selectedProject.id].specifications?.projectType}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-600">{t.modal.location}</span>
                          </div>
                          <p className="text-gray-900">{t.projects[selectedProject.id].specifications?.location}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-600">{t.modal.duration}</span>
                          </div>
                          <p className="text-gray-900">{t.projects[selectedProject.id].specifications?.duration}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <Ruler className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-600">{t.modal.gardenSize}</span>
                          </div>
                          <p className="text-gray-900">{t.projects[selectedProject.id].specifications?.gardenSize}</p>
                        </div>
                      </div>
                      {t.projects[selectedProject.id].specifications?.challenges && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-700">{t.modal.keyChallenges}</span>
                          <p className="text-gray-900 mt-1">{t.projects[selectedProject.id].specifications?.challenges}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Process and Results */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Process */}
                    {t.projects[selectedProject.id].process && (
                      <div>
                        <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{t.modal.process}</h3>
                        <ul className="space-y-3">
                          {t.projects[selectedProject.id].process?.map((step, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center text-sm font-medium">
                                {index + 1}
                              </span>
                              <span className="text-gray-700">{step}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Results */}
                    {t.projects[selectedProject.id].results && (
                      <div>
                        <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{t.modal.results}</h3>
                        <ul className="space-y-3">
                          {t.projects[selectedProject.id].results?.map((result, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-5 h-5 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </span>
                              <span className="text-gray-700">{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Before/After */}
                  {t.projects[selectedProject.id].beforeAfter && (
                    <div className="mb-8">
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{t.modal.beforeAfter}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-2">{t.modal.before}</h4>
                          <p className="text-gray-800">{t.projects[selectedProject.id].beforeAfter?.before}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-2">{t.modal.after}</h4>
                          <p className="text-gray-800">{t.projects[selectedProject.id].beforeAfter?.after}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Client Feedback */}
                  {/* Temporarily hidden - can be restored by uncommenting below */}
                  {/*
                  {t.projects[selectedProject.id].clientFeedback && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{t.modal.clientFeedback}</h3>
                      <blockquote className="text-gray-700 italic">
                        &ldquo;{t.projects[selectedProject.id].clientFeedback}&rdquo;
                      </blockquote>
                    </div>
                  )}
                  */}
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </section>
  )
} 